import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import UploadZone from '../components/ui/upload-zone';

export default function LogForm({ onSuccess }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [smokeActive, setSmokeActive] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        author: 'Safety Officer',
        content: '',
        category: 'Safety',
        location: '',
        priority: 'Medium',
        photo_url: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSmokeTrigger = () => {
        setSmokeActive(true);
        setTimeout(() => setSmokeActive(false), 2000); // Reset after animation
    };

    const handleUpload = async (file) => {
        if (!file) return;
        setLoading(true);
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const response = await fetch('http://localhost:3001/api/upload/', {
                method: 'POST',
                body: uploadData,
            });
            const data = await response.json();
            setFormData(prev => ({ ...prev, photo_url: data.data.url }));
        } catch (err) {
            console.error("Upload failed", err);
            alert("Photo upload failed");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccess(false);

        // TRIGGER MASSIVE PINK SMOKE
        handleSmokeTrigger();

        try {
            const response = await fetch('http://localhost:3001/api/logs/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) throw new Error('Failed to submit');

            setSuccess(true);
            if (onSuccess) setTimeout(() => onSuccess(), 1500);

            setFormData({
                title: '',
                author: 'Safety Officer',
                content: '',
                category: 'Safety',
                location: '',
                priority: 'Medium',
                photo_url: '',
            });
        } catch (error) {
            console.error(error);
            alert(t('error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative">
            {/* The Smoke Container */}
            <div className={`absolute inset-0 pointer-events-none ${smokeActive ? 'smoke-active' : ''}`} />

            <Card className="w-full max-w-md mx-auto shadow-lg border-none sm:border bg-white/90 dark:bg-zinc-900/90 backdrop-blur">
                <CardHeader>
                    <CardTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                        {t('new_log', 'New Safety Report')}
                    </CardTitle>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="title" className="text-sm font-medium opacity-70">{t('title', 'Title')}</Label>
                            <Input
                                id="title"
                                name="title"
                                placeholder={t('title_placeholder', "What happened?")}
                                required
                                value={formData.title}
                                onChange={handleChange}
                                className="text-lg font-medium border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-600 bg-transparent"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="author" className="text-sm font-medium opacity-70">{t('author', 'Reporter')}</Label>
                            <Input
                                id="author"
                                name="author"
                                placeholder={t('author_placeholder', 'Your name')}
                                required
                                value={formData.author}
                                onChange={handleChange}
                                className="bg-slate-50 dark:bg-zinc-800 border-transparent"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="category" className="text-xs uppercase tracking-wider opacity-60">{t('category', 'Category')}</Label>
                                <Select id="category" name="category" value={formData.category} onChange={handleChange}
                                    className="bg-slate-50 dark:bg-zinc-800 border-transparent focus:ring-2 ring-blue-500/20">
                                    <option value="Safety">🦺 {t('safety', 'Safety')}</option>
                                    <option value="Security">🛡️ {t('security', 'Security')}</option>
                                    <option value="Maintenance">🔧 {t('maintenance', 'Maintenance')}</option>
                                    <option value="IT">💻 {t('it', 'IT')}</option>
                                    <option value="Environmental">🌱 {t('environmental', 'Environmental')}</option>
                                    <option value="Quality">⭐ {t('quality', 'Quality')}</option>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="priority" className="text-xs uppercase tracking-wider opacity-60">{t('priority', 'Priority')}</Label>
                                <Select id="priority" name="priority" value={formData.priority} onChange={handleChange}
                                    className="bg-slate-50 dark:bg-zinc-800 border-transparent focus:ring-2 ring-blue-500/20">
                                    <option value="Low">🟢 {t('low', 'Low')}</option>
                                    <option value="Medium">🟡 {t('medium', 'Medium')}</option>
                                    <option value="High">🟠 {t('high', 'High')}</option>
                                    <option value="Critical">🔴 {t('critical', 'Critical')}</option>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="location" className="text-sm font-medium opacity-70">{t('location', 'Location')}</Label>
                            <Input
                                id="location"
                                name="location"
                                placeholder={t('location_placeholder', "Where did this occur?")}
                                required
                                value={formData.location}
                                onChange={handleChange}
                                className="bg-slate-50 dark:bg-zinc-800 border-transparent"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="content" className="text-sm font-medium opacity-70">{t('description', 'Description')}</Label>
                            <Textarea
                                id="content"
                                name="content"
                                placeholder={t('desc_placeholder', "Describe what happened in detail...")}
                                required
                                value={formData.content}
                                onChange={handleChange}
                                className="min-h-[100px] bg-slate-50 dark:bg-zinc-800 border-transparent resize-none"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-medium opacity-70">{t('evidence', 'Evidence Photo')}</Label>
                            <UploadZone
                                onFileSelect={handleUpload}
                                previewUrl={formData.photo_url ? `http://localhost:3001${formData.photo_url}` : null}
                                onClear={() => setFormData(prev => ({ ...prev, photo_url: '' }))}
                                loading={loading}
                            />
                        </div>

                    </CardContent>
                    <CardFooter className="flex flex-col gap-3 pb-8">
                        <Button
                            type="submit"
                            className="w-full h-12 text-lg font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transition-all active:scale-95"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    {t('sending', 'Submitting Report...')}
                                </>
                            ) : (
                                t('submit', 'Submit Safety Report')
                            )}
                        </Button>
                        {success && (
                            <div className="p-3 text-sm text-green-700 bg-green-100 rounded-md w-full text-center font-medium">
                                ✨ Safety Report Submitted Successfully ✨
                                <br />
                                <span className="text-xs opacity-75">
                                    {formData.priority === 'High' || formData.priority === 'Critical' ? 
                                        'High priority - RAYVN and email notifications sent' : 
                                        'Report logged and notifications sent'}
                                </span>
                            </div>
                        )}
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}